import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import {InputText} from "primereact/inputtext";
import LicenseDialogFields from "../field/LicenseDialogFields";
import Administration from "../../session/Administration";
import {
	Application,
	ApplicationCheck,
	ApplicationCheckInterface,
	ApplicationCheckMode,
	ConfigurationType,
	License
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import Info, {InfoType} from "../../../shared/components/Info";
import FunctionHelper from "../../../shared/helper/FunctionHelper";

export class LicenseDialogDefinition extends AdminDialog<License,
	AdminDialogProperties<License>, AdminDialogState<License>> {
	public renderContents(): ReactNode {
		let parameter: License | undefined = this.state.parameter;
		let errors: Map<LicenseDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-licensee">
						<Translate {...this.props} data="license.licensee" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={License.getLicenseeDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<InputText id="control-licensee"
								   value={FunctionHelper.applyValue(parameter?.licensee, License.getLicenseeDefault())}
								   onInput={(e) => this.onChange(
									   LicenseDialogFields.LICENSEE, e.currentTarget.value
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-key">
						<Translate {...this.props} data="license.key.label" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={License.getKeyDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<InputText id="control-key"
								   className={errors.has(LicenseDialogFields.KEY) ? " p-invalid" : ""}
								   value={FunctionHelper.applyValue(parameter?.key, License.getKeyDefault())}
								   placeholder={Translate.value("license.key.placeholder", {ns: "dialog"})}
								   tooltip={errors.has(LicenseDialogFields.KEY) ? Translate.value(
									   errors.get(LicenseDialogFields.KEY)!, {ns: "message"}
								   ) : ""}
								   onInput={(e) => this.onChange(
									   LicenseDialogFields.KEY, e.currentTarget.value
								   )}/>
						{this.renderFieldError(errors.has(LicenseDialogFields.KEY) ?
							Translate.value(
								errors.get(LicenseDialogFields.KEY)!, {ns: "message"}
							) : ""
						)}
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["license-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.license.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.admin.license.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.SUCCESS}>
						<Translate {...this.props} data={"dialog.admin.license.hint"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-key"}/>
					<Translate {...this.props} data="license.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: LicenseDialogFields, value: any): void {
		let parameter: License | undefined = this.state.parameter as License;
		let errors: Map<LicenseDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case LicenseDialogFields.LICENSEE:
				parameter.licensee = value;
				break;
			case LicenseDialogFields.KEY:
				let inputVal: Array<string> = value.split('');
				let keys: Array<string> = [];

				for (let part of inputVal) {
					if (part.search(/^([^a-zA-Z0-9])+$/)) {
						keys.push(part);
					}
				}

				let licenseKey = '';

				for (let [index, keyPart] of keys.entries()) {
					if ((index % 6) === 0 && index !== 0) {
						licenseKey += '-';
					}

					licenseKey += keyPart;
				}

				if (FunctionHelper.isEmpty(licenseKey)) {
					errors.set(field, "errors.fields.set");
				} else if (licenseKey.length !== 34) {
					errors.set(field, "errors.admin.license");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.key = licenseKey.toUpperCase();
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let applicationConfiguration: Application = (
			await administration.getConfiguration(ConfigurationType.Application) as Application
		).clone();
		applicationConfiguration.license = this.state.parameter;

		try {
			await administration.updateConfiguration(
				ConfigurationType.Application, applicationConfiguration, [
					ApplicationCheck.fromJson({
						checkType: ApplicationCheckMode.License
					} as ApplicationCheckInterface)
				]);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.license", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.LICENSE;
	}
}

const LicenseDialog = React.memo(LicenseDialogDefinition,
	(
		prevProps: Readonly<AdminDialogProperties<License>>,
		nextProps: Readonly<AdminDialogProperties<License>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default LicenseDialog;